import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowUpRight, CalendarClock } from 'lucide-react';
import { createCategorySlug } from '../utils/urlHelpers';
import {
  aggregateCategoryForums,
  buildCategoriesListSeoParagraph,
  formatCategoryLastUpdate,
  latestForumUpdateIso,
} from '../utils/categoryAggregates';
import type { ForumBase } from '../types/forum';

interface CategoryStats {
  totalMembers: number;
  previousTotalMembers: number;
}

interface CategoryItem {
  id: string;
  name: string;
}

interface Props {
  categories: CategoryItem[];
  categoryStats: Record<string, CategoryStats>;
  forums: ForumBase[];
  darkMode: boolean;
}

const CategoriesPageHero: React.FC<Props> = ({ categories, categoryStats, forums, darkMode }) => {
  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastUpdateIso = useMemo(() => latestForumUpdateIso(forums), [forums]);

  const growing = useMemo(() => {
    return categories
      .map((c) => {
        const s = categoryStats[c.name];
        if (!s || s.previousTotalMembers <= 0) return null;
        const pct =
          ((s.totalMembers - s.previousTotalMembers) / s.previousTotalMembers) * 100;
        if (pct <= 0) return null;
        return { ...c, growthPercent: pct };
      })
      .filter((x): x is CategoryItem & { growthPercent: number } => x !== null)
      .sort((a, b) => b.growthPercent - a.growthPercent)
      .slice(0, 5);
  }, [categories, categoryStats]);

  const seoParagraph = useMemo(
    () =>
      buildCategoriesListSeoParagraph(categories.length, agg, lastUpdateIso),
    [categories.length, agg, lastUpdateIso]
  );

  const card = darkMode
    ? 'bg-gray-800/80 border border-gray-700'
    : 'bg-white shadow-sm border border-indigo-100';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10 items-stretch">
      <section className={`rounded-xl p-4 sm:p-6 h-full ${card}`} aria-labelledby="growing-categories-heading">
        <h2
          id="growing-categories-heading"
          className={`text-lg font-semibold flex items-center gap-2 mb-4 ${
            darkMode ? 'text-white' : 'text-indigo-900'
          }`}
        >
          <TrendingUp className="text-green-500" size={22} />
          En çok büyüyen kategoriler
        </h2>
        {growing.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Karşılaştırma verisi olan kategorilerde artış henüz görünmüyor.
          </p>
        ) : (
          <ul className="space-y-3">
            {growing.map((c, i) => (
              <li key={c.id}>
                <Link
                  to={`/category/${createCategorySlug(c.name)}`}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg transition-colors ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'
                  }`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span
                      className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        darkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {c.name}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-green-500 text-sm font-medium shrink-0">
                    <ArrowUpRight size={14} />
                    +{c.growthPercent.toFixed(1)}% üye
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`rounded-xl p-4 sm:p-6 h-full ${card}`} aria-labelledby="categories-overview-heading">
        <h2
          id="categories-overview-heading"
          className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-indigo-900'}`}
        >
          Kategoriler özeti
        </h2>
        <p className={`text-sm mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <CalendarClock size={16} className="shrink-0" />
          Son güncelleme: {formatCategoryLastUpdate(lastUpdateIso)}
        </p>
        <dl className={`grid grid-cols-2 gap-3 text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Kategori</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {categories.length}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Aktif forum</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.forumCount.toLocaleString('tr-TR')}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Toplam üye</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.totalMembers.toLocaleString('tr-TR')}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Toplam ileti</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.totalPosts.toLocaleString('tr-TR')}
            </dd>
          </div>
        </dl>
        <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {seoParagraph}
        </p>
      </section>
    </div>
  );
};

export default CategoriesPageHero;
